import { useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useAdminEmployees } from '@/common/hooks/useAdminEmployees'
import { Table } from '@/common/components/table'
import { LoadingSpinner } from '@/common/components/loading-spinner'
import { Text } from '@/common/components/text'
import { Stack } from '@/common/components/stack'
import { Badge } from '@/common/components/badge'
import { Checkbox } from '@/common/components/checkbox'
import { colors } from '@/core/styles/tokens'

/** BambooHR statuses arrive lowercased ("terminated", "inactive"). */
function formatStatus(status: string) {
  return status.charAt(0).toUpperCase() + status.slice(1)
}

function AdminUsers() {
  const [search, setSearch] = useState('')
  const [includeInactive, setIncludeInactive] = useState(false)
  const { data: employees, isLoading, error } = useAdminEmployees(search || undefined, includeInactive)

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200 }}>
      <Stack gap="16">
        <Text size="xl" weight="bold">
          Users
        </Text>

        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: 6,
              border: `1px solid ${colors.gray20}`,
              fontSize: 14,
              width: 320,
            }}
          />
          <Checkbox
            checked={includeInactive}
            onCheckedChange={(checked) => setIncludeInactive(checked === true)}
            label="Include former employees"
          />
        </div>

        {error && (
          <Text color="error">Failed to load users: {error.message}</Text>
        )}

        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <Table.Root size="md" stickyHeader>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Email</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Department</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Job Title</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {employees && employees.length > 0 ? (
                employees.map((emp) => (
                  <Table.Row key={emp.id}>
                    <Table.Cell>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <Link
                          to="/org/users/$employeeId"
                          params={{ employeeId: String(emp.id) }}
                          style={{ color: colors.sea, fontWeight: 500 }}
                        >
                          {emp.first_name} {emp.last_name}
                        </Link>
                        {emp.employment_status !== 'active' && (
                          <Badge color="caution" size="sm">
                            {formatStatus(emp.employment_status)}
                          </Badge>
                        )}
                        {emp.timesheet_exempt && (
                          <Badge color="neutral" size="sm">
                            Timesheet exempt
                          </Badge>
                        )}
                      </span>
                    </Table.Cell>
                    <Table.Cell>{emp.email ?? '—'}</Table.Cell>
                    <Table.Cell>{emp.department ?? '—'}</Table.Cell>
                    <Table.Cell>{emp.job_title ?? '—'}</Table.Cell>
                  </Table.Row>
                ))
              ) : (
                <Table.Row>
                  <Table.Cell colSpan={4} align="center">
                    <Text color="muted">No users found</Text>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table.Root>
        )}

        {employees && (
          <Text size="sm" color="muted">
            {employees.length} user{employees.length !== 1 ? 's' : ''}
          </Text>
        )}
      </Stack>
    </div>
  )
}

export const Route = createFileRoute('/org/users/')({
  component: AdminUsers,
})
